// 外包 IP 工作台详情抽屉布局 hook。
// 参考内包 apps/mpc-inner/src/featrures/IPCooperationDemand/Drawer/hooks/useHostDrawerLayout.ts：
// 动态测量抽屉 header 实际高度，注入 --ip-header-h CSS 变量，供 TabsAnchor 锚点 tabs sticky 吸附偏移。
// 外包无 SplitOrderTree 层级侧栏，故去掉 --content-w / --history-w 变量。

import { useEffect, useState, type CSSProperties } from 'react';

/** 抽屉 header 选择器（CSS Modules 哈希名，需用属性包含匹配，对齐内包 useHostDrawerLayout） */
const DRAWER_HEADER_SELECTOR = '.ip-out-detail-drawer [class*="container-drawer__header"]';

interface UseDetailDrawerLayoutParams {
  /** 抽屉是否可见（visible 后 header 才挂载，才能测量高度） */
  visible?: boolean;
}

interface UseDetailDrawerLayoutReturn {
  /** 注入抽屉的 CSS 变量（--ip-header-h 供 tabs sticky 吸附偏移） */
  drawerCssVars: CSSProperties;
  /** header 实测高度（rem 数，供 TabsAnchor offsetTop 计算锚点滚动偏移，避开吸顶 tab 遮挡） */
  headerHeight: number;
}

/**
 * 读当前 rem 基准（html font-size，1rem=多少 px）。
 * ProScreenAdapter（PxToRemWrapper rootValue + range 屏幕宽度缩放）下，html font-size 基准 / viewBase 随屏幕变化，
 * header 的 rem 高度会随之变化——故不可写死固定 px，须动态读 rem 值换算。
 */
function readRemPx(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

export function useDetailDrawerLayout({
  visible,
}: UseDetailDrawerLayoutParams): UseDetailDrawerLayoutReturn {
  // header 高度（rem 数）：初值 0 不写死；measure 后换算 rem，并随 header 高度 / rem 基准变化动态更新
  const [headerHeight, setHeaderHeight] = useState(0);

  // 测量 header 实际高度，驱动 tabs sticky top（header 内容随节点/状态/屏幕缩放变化，固定偏移会露出缝隙）
  useEffect(() => {
    if (!visible) return;
    let raf = 0;
    let ro: ResizeObserver | null = null;
    const bind = () => {
      const el = document.querySelector(DRAWER_HEADER_SELECTOR) as HTMLElement | null;
      if (!el) return;
      ro?.disconnect();
      // offsetHeight 为布局 px，÷ 实时 rem(px) → rem 数，注入 --ip-header-h(rem)，随 viewBase/rem 基准同步
      setHeaderHeight(el.offsetHeight / readRemPx());
      ro = new ResizeObserver(() => setHeaderHeight(el.offsetHeight / readRemPx()));
      ro.observe(el);
    };
    raf = requestAnimationFrame(bind);
    const t = window.setTimeout(bind, 80);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      ro?.disconnect();
    };
  }, [visible]);

  const drawerCssVars: CSSProperties = {
    '--ip-header-h': `${headerHeight}rem`,
  } as CSSProperties;

  return { drawerCssVars, headerHeight };
}
